import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  getTeacherCohortAssignmentProgress,
  getTeacherCohorts,
  type TeacherCohortRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import {
  aggregateProgress,
  cohortPassRates,
  lessonProgressRowFromAssignment,
  lessonRowsFromBundles,
  teacherAssignmentReviewUrl,
  type CohortProgressBundle,
} from "./submissions/submissionsAggregate";
import { CohortStudentProgressSection, type CohortProgressView } from "./CohortStudentProgressSection";
import { CohortEventLogSection } from "./CohortEventLogSection";
import { parseCohortProgressView } from "./submissions/cohortProgressUrl";
import ProgressDonut from "./charts/ProgressDonut";
import StackedProgressBar from "./charts/StackedProgressBar";
import {
  ASSIGNMENT_PROGRESS_EMOJI,
  formatProgressLabel,
} from "../student/assignmentProgressStatus";

const ALL_COHORTS = "all";

function pct(n: number | null): string {
  if (n === null) return "-";
  return `${n}%`;
}

function KpiCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint: string;
  accent?: "green" | "amber" | "forest" | "slate";
}) {
  return (
    <article className={`submissions-kpi submissions-kpi--${accent ?? "forest"}`}>
      <p className="submissions-kpi-label">{label}</p>
      <p className="submissions-kpi-value">{value}</p>
      <p className="submissions-kpi-hint">{hint}</p>
    </article>
  );
}

export default function TeacherSubmissionsPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const cohortFromUrl = searchParams.get("cohort")?.trim() ?? "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cohorts, setCohorts] = useState<TeacherCohortRecord[]>([]);
  const [bundles, setBundles] = useState<CohortProgressBundle[]>([]);
  const [cohortFilter, setCohortFilter] = useState(ALL_COHORTS);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [progressView, setProgressView] = useState<CohortProgressView>(() =>
    parseCohortProgressView(searchParams.get("view"))
  );

  const reload = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getTeacherCohorts(bearer, { limit: 100, offset: 0 });
      const eligible = list.cohorts.filter((c) => includeArchived || c.status === "active");
      setCohorts(eligible);

      const results = await Promise.allSettled(
        eligible.map(async (c) => {
          const prog = await getTeacherCohortAssignmentProgress(bearer, c.id);
          return {
            cohortId: c.id,
            cohortLabel: c.label,
            courseTitle: c.courseTitle,
            status: c.status,
            rosterTotal: prog.rosterTotal,
            assignments: prog.assignments,
          } satisfies CohortProgressBundle;
        })
      );

      const ok: CohortProgressBundle[] = [];
      const failed: string[] = [];
      for (const r of results) {
        if (r.status === "fulfilled") ok.push(r.value);
        else failed.push(r.reason instanceof Error ? r.reason.message : "load failed");
      }
      setBundles(ok);
      if (failed.length > 0 && ok.length === 0) {
        setError(failed[0] ?? "Could not load progress");
      } else if (failed.length > 0) {
        setError(`Some cohorts failed to load (${failed.length}). Showing partial data.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load dashboard");
      setBundles([]);
      setCohorts([]);
    } finally {
      setLoading(false);
    }
  }, [bearer, includeArchived]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (cohortFilter !== ALL_COHORTS && !cohorts.some((c) => c.id === cohortFilter)) {
      setCohortFilter(ALL_COHORTS);
    }
  }, [cohorts, cohortFilter]);

  useEffect(() => {
    if (!cohortFromUrl || cohorts.length === 0) return;
    if (cohorts.some((c) => c.id === cohortFromUrl)) {
      setCohortFilter(cohortFromUrl);
    }
  }, [cohortFromUrl, cohorts]);

  function updateProgressUrl(nextCohort: string, nextView: CohortProgressView) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextCohort === ALL_COHORTS) {
          next.delete("cohort");
          next.delete("view");
        } else {
          next.set("cohort", nextCohort);
          if (nextView === "by-assignment") next.set("view", "by-assignment");
          else next.delete("view");
        }
        return next;
      },
      { replace: true }
    );
  }

  function selectCohortFilter(nextCohort: string) {
    setCohortFilter(nextCohort);
    updateProgressUrl(nextCohort, progressView);
  }

  function selectProgressView(nextView: CohortProgressView) {
    setProgressView(nextView);
    if (cohortFilter !== ALL_COHORTS) {
      updateProgressUrl(cohortFilter, nextView);
    }
  }

  useEffect(() => {
    setProgressView(parseCohortProgressView(searchParams.get("view")));
  }, [searchParams]);

  const filteredBundles = useMemo(
    () => (cohortFilter === ALL_COHORTS ? bundles : bundles.filter((b) => b.cohortId === cohortFilter)),
    [bundles, cohortFilter]
  );

  const totals = useMemo(() => aggregateProgress(filteredBundles), [filteredBundles]);
  const lessonRows = useMemo(
    () => lessonRowsFromBundles(filteredBundles, {
      sort: cohortFilter === ALL_COHORTS ? "needs-attention" : "plan-order",
    }),
    [filteredBundles, cohortFilter]
  );
  const needsAttentionRows = useMemo(
    () => lessonRowsFromBundles(filteredBundles, { sort: "needs-attention" }),
    [filteredBundles]
  );
  const cohortRates = useMemo(() => cohortPassRates(filteredBundles), [filteredBundles]);
  const needsAttention = useMemo(
    () => needsAttentionRows.filter((r) => r.needsHelp > 0).slice(0, 6),
    [needsAttentionRows]
  );

  const filterLabel =
    cohortFilter === ALL_COHORTS
      ? `All cohorts (${filteredBundles.length})`
      : cohorts.find((c) => c.id === cohortFilter)?.label ?? "Cohort";

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Analytics</p>
          <h1 className="teacher-page-title">Progress</h1>
          <p className="teacher-page-lede">
            Live picture of who&apos;s passing, who&apos;s stuck, and where to jump in, across every assigned lesson.
          </p>
        </div>
        <div className="submissions-hero-filter">
          <label className="field submissions-filter-field">
            <span className="field-label">Cohort</span>
            <select
              className="input submissions-filter-select"
              value={cohortFilter}
              onChange={(ev) => selectCohortFilter(ev.target.value)}
              disabled={loading}
            >
              <option value={ALL_COHORTS}>All cohorts</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} · {c.courseTitle}
                  {c.status === "archived" ? " (archived)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="submissions-archived-toggle muted small">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(ev) => setIncludeArchived(ev.target.checked)}
              disabled={loading}
            />
            Include archived cohorts
          </label>
        </div>
      </header>

      {error ? (
        <div className="warn-banner submissions-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted submissions-loading">Loading dashboard…</p>
      ) : filteredBundles.length === 0 ? (
        <section className="teacher-panel submissions-empty">
          <h2 className="teacher-panel-title">No cohort data yet</h2>
          <p className="muted">
            Create a cohort, assign lessons, and have students click Check My Work. Then KPIs and charts will populate here.
          </p>
          <Link to="/teacher/cohorts" className="btn primary">
            Set up a cohort
          </Link>
        </section>
      ) : (
        <>
          <p className="submissions-scope muted small">
            Showing <strong>{filterLabel}</strong>
            {totals.total > 0 ? (
              <>
                {" "}
                · {totals.total.toLocaleString()} student-assignment pairings tracked
              </>
            ) : null}
          </p>

          <div className="submissions-kpi-grid">
            <KpiCard
              accent="green"
              label="Pass rate"
              value={pct(totals.passRate)}
              hint={`${totals.complete.toLocaleString()} complete of ${totals.total.toLocaleString()} tracked`}
            />
            <KpiCard
              accent="forest"
              label="Active roster"
              value={totals.rosterStudents.toLocaleString()}
              hint="Students enrolled in filtered cohort(s)"
            />
            <KpiCard
              accent="amber"
              label={`${ASSIGNMENT_PROGRESS_EMOJI.needs_help} Needs help`}
              value={totals.needsHelp.toLocaleString()}
              hint="Last Check My Work did not pass all required checks"
            />
            <KpiCard
              accent="slate"
              label={`${ASSIGNMENT_PROGRESS_EMOJI.in_progress} In progress`}
              value={totals.inProgress.toLocaleString()}
              hint="Opened the lesson but no failed Check My Work yet"
            />
            <KpiCard
              accent="slate"
              label={`${ASSIGNMENT_PROGRESS_EMOJI.not_started} Not started`}
              value={totals.notStarted.toLocaleString()}
              hint="Have not opened the lesson workspace yet"
            />
          </div>

          {cohortFilter !== ALL_COHORTS ? (
            <CohortStudentProgressSection
              cohortId={cohortFilter}
              view={progressView}
              onViewChange={selectProgressView}
            />
          ) : null}

          <div className="submissions-charts-grid">
            <section className="submissions-chart-panel">
              <h2 className="submissions-chart-title">Completion mix</h2>
              <p className="muted small submissions-chart-sub">
                Share of student-assignment slots by status
              </p>
              <ProgressDonut
                complete={totals.complete}
                inProgress={totals.inProgress}
                needsHelp={totals.needsHelp}
                notStarted={totals.notStarted}
                passRate={totals.passRate}
              />
            </section>

            <section className="submissions-chart-panel submissions-chart-panel--wide">
              <h2 className="submissions-chart-title">
                {cohortFilter === ALL_COHORTS && filteredBundles.length > 1
                  ? "Pass rate by cohort"
                  : "Pass rate by assignment"}
              </h2>
              <p className="muted small submissions-chart-sub">
                {cohortFilter === ALL_COHORTS && filteredBundles.length > 1
                  ? "Compare cohorts at a glance"
                  : "Each bar is the % of roster that passed"}
              </p>
              <div className="submissions-bars">
                {cohortFilter === ALL_COHORTS && filteredBundles.length > 1 ? (
                  cohortRates.map((c) => (
                    <div key={c.cohortId} className="submissions-cohort-bar-row">
                      <button
                        type="button"
                        className="submissions-cohort-bar-label"
                        onClick={() => selectCohortFilter(c.cohortId)}
                        title={`Filter to ${c.cohortLabel}`}
                      >
                        <span className="submissions-cohort-bar-name">{c.cohortLabel}</span>
                        <span className="muted small">{c.courseTitle}</span>
                      </button>
                      <div className="submissions-cohort-bar-track">
                        <span
                          className="submissions-cohort-bar-fill"
                          style={{ width: `${c.passRate ?? 0}%` }}
                        />
                      </div>
                      <span className="submissions-cohort-bar-pct">{pct(c.passRate)}</span>
                    </div>
                  ))
                ) : (
                  lessonRows.slice(0, 10).map((r) => (
                    <StackedProgressBar
                      key={`${r.cohortId}-${r.assignmentId}`}
                      label={cohortFilter === ALL_COHORTS ? `${r.lessonTitle} (${r.cohortLabel})` : r.lessonTitle}
                      complete={r.complete}
                      inProgress={r.inProgress}
                      needsHelp={r.needsHelp}
                      notStarted={r.notStarted}
                    />
                  ))
                )}
                {lessonRows.length === 0 && cohortFilter !== ALL_COHORTS ? (
                  <p className="muted">No assignments with progress data yet.</p>
                ) : null}
              </div>
            </section>
          </div>

          {needsAttention.length > 0 ? (
            <section className="submissions-attention">
              <h2 className="submissions-chart-title">Students need help here</h2>
              <p className="muted small">Lessons with the most students whose last Check My Work did not pass</p>
              <div className="submissions-attention-grid">
                {needsAttention.map((r) => {
                  const reviewUrl = teacherAssignmentReviewUrl(r.cohortId, r);
                  if (!reviewUrl) return null;
                  return (
                  <Link
                    key={`${r.cohortId}-${r.assignmentId}`}
                    to={reviewUrl}
                    className="submissions-attention-card"
                  >
                    <span className="submissions-attention-count">{r.needsHelp}</span>
                    <span className="submissions-attention-lesson">{r.lessonTitle}</span>
                    <span className="muted small">{r.cohortLabel}</span>
                  </Link>
                  );
                })}
              </div>
            </section>
          ) : null}

          {cohortFilter === ALL_COHORTS ? (
            <section className="teacher-panel teacher-panel--flush submissions-table-panel">
              <h2 className="teacher-panel-title teacher-pad">All assignments in scope</h2>
              <p className="muted small teacher-pad" style={{ paddingTop: 0, marginTop: 0 }}>
                Drill into every Check My Work attempt, including failed tries.
              </p>
              <div className="teacher-table-wrap">
                <table className="teacher-table submissions-scope-table">
                  <thead>
                    <tr>
                      <th>Assignment</th>
                      <th>{formatProgressLabel("complete", { emoji: false })}</th>
                      <th>{formatProgressLabel("in_progress", { emoji: false })}</th>
                      <th>{formatProgressLabel("needs_help", { emoji: false })}</th>
                      <th>{formatProgressLabel("not_started", { emoji: false })}</th>
                      <th>Pass rate</th>
                      <th />
                    </tr>
                  </thead>
                  {filteredBundles.map((bundle) => (
                    <tbody key={bundle.cohortId} className="submissions-cohort-table-group">
                      <tr className="submissions-cohort-table-head">
                        <td colSpan={7}>
                          <strong>{bundle.cohortLabel}</strong>
                          <span className="muted small"> · {bundle.courseTitle}</span>
                          {bundle.status === "archived" ? (
                            <span className="muted small"> · Concluded</span>
                          ) : null}
                        </td>
                      </tr>
                      {bundle.assignments.map((a) => {
                        const r = lessonProgressRowFromAssignment(bundle, a);
                        const reviewUrl = teacherAssignmentReviewUrl(r.cohortId, r);
                        return (
                          <tr key={r.assignmentId} className="submissions-cohort-table-row">
                            <td>
                              <strong>{r.lessonTitle}</strong>
                              {r.itemKind === "quiz" ? <span className="muted small"> · Quiz</span> : null}
                            </td>
                            <td>
                              <span className="submissions-table-pill submissions-table-pill--complete">{r.complete}</span>
                            </td>
                            <td>
                              <span className="submissions-table-pill submissions-table-pill--in-progress">{r.inProgress}</span>
                            </td>
                            <td>
                              <span className="submissions-table-pill submissions-table-pill--needs-help">{r.needsHelp}</span>
                            </td>
                            <td>
                              <span className="submissions-table-pill submissions-table-pill--not-started">{r.notStarted}</span>
                            </td>
                            <td>{pct(r.passRate)}</td>
                            <td className="teacher-table-actions">
                              {reviewUrl ? (
                                <Link to={reviewUrl} className="btn secondary tight">
                                  Review
                                </Link>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ))}
                </table>
              </div>
            </section>
          ) : null}
        </>
      )}

      {!loading && cohorts.length > 0 ? (
        <CohortEventLogSection
          bearer={bearer}
          cohorts={cohorts}
          cohortId={cohortFilter === ALL_COHORTS ? null : cohortFilter}
          lockCohortFilter
        />
      ) : null}
    </div>
  );
}
